import { useQuery } from 'react-query';
import { queryKeys } from './queryKeys';
import { useFpId } from '../../../../../../hooks/useFpId';
import { urlWithParams } from '../../../../../../../CommonUtils/urlWithParams';
import { authFetch } from '../../../../../../../CommonUtils/authFetch';
import { useClientId } from '../../../../../../../_hooks/useClientId';

export enum FlowType {
  Node = 'Node',
  Edge = 'Edge',
  Group = 'Group',
}

export enum ItemType {
  Principal = 'Principal',
  Spouse = 'Spouse',
  Beneficiaries = 'Beneficiaries',
  CharityBeneficiaries = 'CharityBeneficiaries',
  TaxesAndFees = 'TaxesAndFees',
}

export interface Item {
  type: ItemType;
  label?: string;
  nameList?: string[];
  value: number;
  additionalInfo?: AdditionalInfoDTO;
}

export interface NodeType {
  type: FlowType.Node;
  items: Item[];
  title?: string;
  value?: number;
}

export interface EdgeType {
  type: FlowType.Edge;
  isSecondDeath?: boolean;
  title: string;
  value?: number;
  additionalInfo?: AdditionalInfoDTO;
}

export interface GroupItem {
  title: string;
  items: (EdgeType | NodeType)[];
}

export interface GroupType {
  type: FlowType.Group;
  groupItems: GroupItem[];
}

export interface AdditionalInfoDTO {
  title: string | null;
  description: Description[];
  explanation?: string;
  showObligationsExceedLiquidAssetsWarning: boolean;
}

interface Description {
  title?: string;
  calculations: Calculation[];
  result?: Calculation;
}

interface Calculation {
  name: string;
  value: number;
  isSubtracted: boolean;
}

export type EstatePlanningFlowChartDTO = NodeType | EdgeType | GroupType;

interface EstatePlanningFlowTableHeaderDTO {
  additionalInfo: null | string;
  collapsed: boolean;
  headerCategory: number;
  headerCategoryText: string;
  headerText: string;
  headerType: number;
  showObligationsExceedLiquidAssetsWarning: boolean;
}

export interface GroupAssetsDTO {
  inOutState: number;
  isLiquid: boolean;
  assetType: number;
  assetTypeText: string;
  assetId: string;
  name: string;
  description: string;
  additionalInfo: string;
  value: number | null;
}

export interface AssetsGroupAssetsDTO {
  assetType: number;
  assetTypeText: string;
  groupAssets: GroupAssetsDTO[];
  total: number;
}

export interface AssetsDTO {
  liquidIlliquidType: number;
  liquidIlliquidTypeText: string;
  groupAssets?: AssetsGroupAssetsDTO[];
  total: number;
}

interface EstateAssets {
  inOutEstate: number;
  inOutEstateText: string;
  liquidAssets: AssetsDTO;
  illiquidAssets: AssetsDTO;
  total: number;
}

export interface EstatePlanningFlowTableDTO {
  header: EstatePlanningFlowTableHeaderDTO;
  inEstateAssets: EstateAssets | null;
  outEstateAssets: EstateAssets | null;
  total: number;
}

export interface TableAdditionalDetailsDTO {
  ageAtPassingText: string;
  resourcesAtPassingText: string;
}

interface StatsItemDTO {
  value: number;
  additionalInfo: AdditionalInfoDTO;
}

export interface StatsDTO {
  adjustedGrossEstate: StatsItemDTO;
  grossEstate: StatsItemDTO;
  taxableEstate: StatsItemDTO;
}

export interface EstatePlanningFlowDTO {
  chart: EstatePlanningFlowChartDTO[];
  table: EstatePlanningFlowTableDTO[];
  tableAdditionalDetails: TableAdditionalDetailsDTO;
  stats: StatsDTO;
}

interface FetchArgs {
  clientId: number;
  fpId: string;
  isSpouse?: boolean;
}

export const fetchEstatePlanningWorkflow = async ({
  clientId,
  fpId,
  isSpouse,
}: FetchArgs): Promise<EstatePlanningFlowDTO> => {
  return authFetch(urlWithParams('/Consumer/EstatePlanningFlow', { clientId, fpId, isSpouse }));
};

interface Params {
  isSpouse?: boolean;
}

export const useEstatePlanningFlowQuery = ({ isSpouse = false }: Params = {}) => {
  const fpId = useFpId();
  const clientId = useClientId();

  return useQuery(
    queryKeys.estatePlanningFlowChart({ clientId, fpId, isSpouse }),

    () => fetchEstatePlanningWorkflow({ clientId, fpId, isSpouse }),

    {
      cacheTime: 0,
    },
  );
};
